import { DialogContent } from '@mui/material';
import { AppBaseButton } from 'components/AppBaseButton/AppBaseButton';
import { EditDialog, EditDialogProps } from 'components/EditDialog/EditDialog';
import { FormStepper, StepModel } from 'components/FormStepper/FormStepper';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface StepperDialogProps<T> extends Omit<EditDialogProps, 'handleFormSubmit'> {
  steps: StepModel[];
  handleSubmit: (onSuccess: (saveValue: T) => void) => () => void;
  onSave: (saveModel: T) => void;
  activeStep: number;
  setActiveStep: (step: number) => void;
  hasOptionalSteps?: boolean;
  stepperWidth?: number | string;
}

export function StepperDialog<T>({
  handleSubmit,
  onSave,
  steps,
  activeStep,
  setActiveStep,
  hasOptionalSteps,
  stepperWidth,
  ...editDialogProps
}: StepperDialogProps<T>): ReactElement {
  const { t } = useTranslation();

  const { children, ...props } = editDialogProps;

  steps.sort(function (a, b) {
    return a.value - b.value;
  });

  const handleStep = (stepVal: number) => {
    // Do not validate if navigating to a previous step.
    if (stepVal <= activeStep) {
      setActiveStep(stepVal);
    } else {
      handleSubmit(() => setActiveStep(stepVal))();
    }
  };

  const isLastStep = () => {
    const numberOfSteps = steps.length;
    const lastStep = numberOfSteps - 1;

    return activeStep === lastStep;
  };

  const handleStepperDialogSubmit = () => {
    // Submit form if on last dialog step.
    if (!isLastStep()) {
      handleSubmit(() => setActiveStep(activeStep + 1))();
    } else {
      handleSubmit(onSave)();
    }
  };

  const renderNextSaveButton: boolean = (hasOptionalSteps && !isLastStep()) || !hasOptionalSteps;

  if (!props.saveActionText) {
    props.saveActionText = t('common.edit-dialog.action-save');
  }

  return (
    <EditDialog
      customButtons={
        <>
          <div
            style={{
              flex: 1,
              flexDirection: 'row',
              justifyContent: 'flex-start',
            }}
          >
            <AppBaseButton variant="outlined" disabled={activeStep === 0} onClick={() => setActiveStep(activeStep - 1)}>
              {t('common.edit-dialog.action-back')}
            </AppBaseButton>
          </div>

          {renderNextSaveButton && (
            <AppBaseButton variant="contained" onClick={handleStepperDialogSubmit}>
              {!isLastStep() || hasOptionalSteps ? t('common.edit-dialog.action-next') : props.saveActionText}
            </AppBaseButton>
          )}

          {hasOptionalSteps && (
            <AppBaseButton variant={isLastStep() ? 'contained' : 'outlined'} onClick={() => handleSubmit(onSave)()}>
              {props.saveActionText}
            </AppBaseButton>
          )}
        </>
      }
      handleFormSubmit={handleStepperDialogSubmit}
      {...props}
    >
      <DialogContent dividers sx={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <FormStepper steps={steps} onChange={handleStep} activeStep={activeStep} width={stepperWidth} />
        {children}
      </DialogContent>
    </EditDialog>
  );
}
